/**
 * Sum Analysis Grand Total Calculator Utility
 *
 * This utility provides a centralized function for calculating the Sum Analysis grand total
 * for groundworks with detailed console logging for debugging and tracking.
 */

import {
  calculateFoundationsGrandTotal,
  calculateCutFillGrandTotal,
  calculateDrainageGrandTotal,
  calculatePlotDrivesGrandTotal,
  calculatePlotServiceGrandTotal,
  calculatePlotPavingsGrandTotal,
  calculateScaffoldGrandTotal,
  calculateGardenGrandTotal,
  calculateRetScreenWallsGrandTotal,
  calculateGEOToberWallGrandTotal,
} from "./summaryCalculations";
import { RateMappingJSON } from "../types/rateMappingTypes";
import { ItemLibraryResponse } from "../api/itemLibrary";
import { RetainingWallsBuildUpSpecification } from "../api/retainingWallsBuildUp";
import { GeoToberWallBuildUpSpecification } from "../api/wallSectionBuildUp";
import { computeDynamicBillSummaryNodeAmount } from "./dynamicBillSummarySerialization";

/**
 * Section definition for sum analysis
 */
export interface SumAnalysisSection {
  name: string;
  stepName: string;
}

/**
 * Detailed breakdown of a single section's calculation
 */
export interface SectionCalculationDetail {
  sectionName: string;
  stepName: string;
  baseAmount: number;
  preliminariesAmount: number;
  ohpAmount: number;
  totalAmount: number;
}

/**
 * Complete sum analysis grand total calculation result
 */
export interface SumAnalysisGrandTotalResult {
  /** Individual section calculation details */
  sections: SectionCalculationDetail[];
  /** Sum of all section base amounts (before preliminaries and OH&P) */
  baseGrandTotal: number;
  /** Total preliminaries amount across all sections */
  totalPreliminaries: number;
  /** Total OH&P amount across all sections */
  totalOhp: number;
  /** Grand total with preliminaries and OH&P applied */
  grandTotalWithAdjustments: number;
  /** Discount amount (if applied) */
  discountAmount: number;
  /** Final tender total (after discount) */
  tenderTotal: number;
  /** Preliminaries percentage used */
  preliminariesPercent: number;
  /** OH&P percentage used */
  ohpPercent: number;
  /** Discount percentage used */
  discountPercent: number;
  /** Timestamp of calculation */
  calculatedAt: Date;
}

/**
 * Options for sum analysis calculation
 */
export interface SumAnalysisCalculationOptions {
  /** Groundworks data from tender state */
  groundworksData: any;
  /** Rate mapping from rate card */
  ratesMapping?: RateMappingJSON | null;
  /** Item library data for rate lookups */
  itemLibraryData?: ItemLibraryResponse;
  /** Preliminaries percentage to apply */
  preliminariesPercent?: number;
  /** OH&P percentage to apply */
  ohpPercent?: number;
  /** Discount percentage to apply */
  discountPercent?: number;
  /** Whether to include preliminaries in calculation */
  includePreliminaries?: boolean;
  /** Whether to include OH&P in calculation */
  includeOhp?: boolean;
  /** Whether to include discount in calculation */
  includeDiscount?: boolean;
  /** Enable detailed console logging */
  enableLogging?: boolean;
  /** Persisted retaining walls build-up specifications from API */
  retWallsBuildUpSpecs?: RetainingWallsBuildUpSpecification[];
  /** Persisted geowall/Tobermore build-up specifications from API */
  geoToberWallBuildUpSpecs?: GeoToberWallBuildUpSpecification[];
}

/**
 * Standard groundworks sections in sum analysis order
 */
export const SUM_ANALYSIS_SECTIONS: SumAnalysisSection[] = [
  { name: "Cut & Fill", stepName: "Cut & Fill" },
  { name: "Foundations", stepName: "Foundations" },
  { name: "Drainage", stepName: "Drainage" },
  { name: "Plot Drives", stepName: "Plot Drives" },
  { name: "Plot Service", stepName: "Plot Service" },
  { name: "Plot Paving", stepName: "Plot Pavings" },
  { name: "Scaffold Platforms", stepName: "Scaffold" },
  { name: "Garden", stepName: "Garden" },
  { name: "Ret & Screen Walls", stepName: "Ret & Screen Walls" },
  { name: "Geowall & Tobermore Walls", stepName: "Geowall & Tobermore Walls" },
];

/**
 * Calculate the grand total for Sum Analysis with detailed logging
 *
 * This function:
 * 1. Calculates base totals for each section
 * 2. Applies preliminaries and OH&P to each section
 * 3. Calculates discount on the final total
 * 4. Logs detailed breakdown to console
 *
 * @param options - Calculation options including data and percentages
 * @returns Complete calculation result with all breakdowns
 */
export function calculateSumAnalysisGrandTotal(
  options: SumAnalysisCalculationOptions
): SumAnalysisGrandTotalResult {
  const {
    groundworksData,
    ratesMapping,
    itemLibraryData,
    preliminariesPercent = 0,
    ohpPercent = 0,
    discountPercent = 0,
    includePreliminaries = false,
    includeOhp = false,
    includeDiscount = false,
    enableLogging = true,
    retWallsBuildUpSpecs,
    geoToberWallBuildUpSpecs,
  } = options;

  const calculatedAt = new Date();

  if (enableLogging) {
    console.log("╔════════════════════════════════════════════════════════════════╗");
    console.log("║        SUM ANALYSIS GRAND TOTAL CALCULATION START              ║");
    console.log("╚════════════════════════════════════════════════════════════════╝");
    console.log(`📅 Calculation Time: ${calculatedAt.toISOString()}`);
    console.log(`⚙️  Settings:`);
    console.log(`   - Preliminaries: ${preliminariesPercent}% (${includePreliminaries ? "INCLUDED" : "EXCLUDED"})`);
    console.log(`   - OH&P: ${ohpPercent}% (${includeOhp ? "INCLUDED" : "EXCLUDED"})`);
    console.log(`   - Discount: ${discountPercent}% (${includeDiscount ? "INCLUDED" : "EXCLUDED"})`);
    console.log("");
  }

  const sections: SectionCalculationDetail[] = [];
  let baseGrandTotal = 0;
  let totalPreliminaries = 0;
  let totalOhp = 0;

  // Calculate each section
  if (enableLogging) {
    console.log("📊 SECTION-BY-SECTION BREAKDOWN:");
    console.log("─────────────────────────────────────────────────────────────────");
  }

  // Apply markup at rate level — same sequential formula as individual bill summaries:
  // OH&P applied first, then Prelims applied to the result.
  const appliedPrelimsPercent = includePreliminaries ? preliminariesPercent : 0;
  const appliedOhpPercent = includeOhp ? ohpPercent : 0;
  const sectionMarkupFactor = (1 + appliedOhpPercent / 100) * (1 + appliedPrelimsPercent / 100);

  SUM_ANALYSIS_SECTIONS.forEach((section) => {
    // Calculate section total with markup baked into the rate (matching bill summaries)
    let totalAmount: number;

    switch (section.stepName) {
      case "Foundations":
        totalAmount = calculateFoundationsGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Foundations' ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring Cut & Fill's own extra items below.
          const foundationsExtraBuckets = (groundworksData as { foundationsExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.foundationsExtraItems;
          if (foundationsExtraBuckets) {
            const extraTotal = Object.values(foundationsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Cut & Fill": {
        totalAmount = calculateCutFillGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Cut & Fill's ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate "Cut & Fill (Extra
        // Items)" section — marked up via sectionMarkupFactor, same as the fixed section,
        //
          const cutFillsExtraBuckets = (groundworksData as { cutFillsExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.cutFillsExtraItems;
          if (cutFillsExtraBuckets) {
            const extraTotal = Object.values(cutFillsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      }
      case "Drainage":
        totalAmount = calculateDrainageGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Drainage's ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring Cut & Fill's/Foundations' own
        // extra items above.
          const drainageExtraBuckets = (groundworksData as { drainageExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.drainageExtraItems;
          if (drainageExtraBuckets) {
            const extraTotal = Object.values(drainageExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Plot Drives":
        totalAmount = calculatePlotDrivesGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Plot Drives' ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring Cut & Fill's/Foundations'/
        // Drainage's own extra items above.
          const plotDrivesExtraBuckets = (groundworksData as { plotDrivesExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.plotDrivesExtraItems;
          if (plotDrivesExtraBuckets) {
            const extraTotal = Object.values(plotDrivesExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Plot Service":
        totalAmount = calculatePlotServiceGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Plot Service's ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring Cut & Fill's/Foundations'/
        // Drainage's/Plot Drives' own extra items above.
          const plotServiceExtraBuckets = (groundworksData as { plotServiceExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.plotServiceExtraItems;
          if (plotServiceExtraBuckets) {
            const extraTotal = Object.values(plotServiceExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Plot Pavings":
        totalAmount = calculatePlotPavingsGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Plot Pavings' ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring the other groundworks Bill
        // Summaries' own extra items above.
          const plotPavingsExtraBuckets = (groundworksData as { plotPavingsExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.plotPavingsExtraItems;
          if (plotPavingsExtraBuckets) {
            const extraTotal = Object.values(plotPavingsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Scaffold":
        totalAmount = calculateScaffoldGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Scaffold's ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring Cut & Fill's/Foundations'/
        // Drainage's/Plot Drives'/Plot Service's own extra items above.
          const scaffoldExtraBuckets = (groundworksData as { scaffoldExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.scaffoldExtraItems;
          if (scaffoldExtraBuckets) {
            const extraTotal = Object.values(scaffoldExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Garden":
        totalAmount = calculateGardenGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          sectionMarkupFactor
        );
        // Fold Garden's ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring the other groundworks Bill
        // Summaries' own extra items above.
          const gardenExtraBuckets = (groundworksData as { gardenExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.gardenExtraItems;
          if (gardenExtraBuckets) {
            const extraTotal = Object.values(gardenExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Ret & Screen Walls":
        totalAmount = calculateRetScreenWallsGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          retWallsBuildUpSpecs,
          sectionMarkupFactor
        );
        // Fold Ret & Screen Walls' ad-hoc extra items (useBillSummaryExtraItems.tsx)
        // into this same section instead of a separate section —
        // marked up the same way (sectionMarkupFactor) —
        // mirroring the other groundworks Bill
        // Summaries' own extra items above.
          const retainingWallsExtraBuckets = (groundworksData as { retainingWallsExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.retainingWallsExtraItems;
          if (retainingWallsExtraBuckets) {
            const extraTotal = Object.values(retainingWallsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      case "Geowall & Tobermore Walls":
        totalAmount = calculateGEOToberWallGrandTotal(
          groundworksData,
          ratesMapping,
          itemLibraryData,
          geoToberWallBuildUpSpecs,
          sectionMarkupFactor
        );
        // Fold Geowall & Tobermore Walls' ad-hoc extra items
        // (useBillSummaryExtraItems.tsx) into this same section instead of a
        // separate section — marked up via sectionMarkupFactor, same as the fixed section,
        // mirroring the other groundworks Bill Summaries' own extra items above.
          const geoWallsExtraBuckets = (groundworksData as { geoWallsExtraItems?: Record<string, Array<Parameters<typeof computeDynamicBillSummaryNodeAmount>[0]>> })?.geoWallsExtraItems;
          if (geoWallsExtraBuckets) {
            const extraTotal = Object.values(geoWallsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0),
              0,
            );
            totalAmount += extraTotal;
          }
        break;
      default:
        totalAmount = 0;
    }

    // Back-derive base/prelims/ohp for the result object (informational only)
    const sectionBaseAmount = sectionMarkupFactor > 0 ? totalAmount / sectionMarkupFactor : totalAmount;
    const preliminariesAmount = sectionBaseAmount * (appliedPrelimsPercent / 100);
    const ohpAmount = sectionBaseAmount * (appliedOhpPercent / 100);

    sections.push({
      sectionName: section.name,
      stepName: section.stepName,
      baseAmount: sectionBaseAmount,
      preliminariesAmount,
      ohpAmount,
      totalAmount,
    });

    baseGrandTotal += sectionBaseAmount;
    totalPreliminaries += preliminariesAmount;
    totalOhp += ohpAmount;

    if (enableLogging) {
      console.log(`\n${section.name}:`);
      console.log(`   Base Amount:         £${sectionBaseAmount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      if (includePreliminaries) {
        console.log(`   + Preliminaries:     £${preliminariesAmount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      }
      if (includeOhp) {
        console.log(`   + OH&P:              £${ohpAmount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      }
      console.log(`   = Section Total:     £${totalAmount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    }
  });

  // Dynamic Bill Summary tabs (user-added "+ Add Tab" sections) each roll into
  // the grand total as their own section, same as SumAnalysis.tsx's on-screen
  // rows — marked up via sectionMarkupFactor, same as every fixed section above.
    const dynamicTabs = (groundworksData?.dynamicBillSummaryTabs ?? []) as Array<{
      label: string;
      nodes: Parameters<typeof computeDynamicBillSummaryNodeAmount>[0][];
    }>;
    dynamicTabs.forEach((tab) => {
      const tabTotal = tab.nodes.reduce((sum, node) => sum + computeDynamicBillSummaryNodeAmount(node, sectionMarkupFactor), 0);
      sections.push({
        sectionName: tab.label,
        stepName: tab.label,
        baseAmount: tabTotal,
        preliminariesAmount: 0,
        ohpAmount: 0,
        totalAmount: tabTotal,
      });
      baseGrandTotal += tabTotal;
    });

  // Grand total with adjustments = sum of all section totals (markup already baked into each).
  // Summing section totals directly matches what SumAnalysis.tsx displays, avoiding any
  // back-derivation error that arises with sequential (non-flat) markup factors.
  const grandTotalWithAdjustments = sections.reduce((s, sec) => s + sec.totalAmount, 0);

  // Calculate discount
  let discountAmount = 0;
  if (includeDiscount && discountPercent > 0 && grandTotalWithAdjustments > 0) {
    discountAmount = grandTotalWithAdjustments * (discountPercent / 100);
  }

  // Final tender total
  const tenderTotal = grandTotalWithAdjustments - discountAmount;

  if (enableLogging) {
    console.log("\n═════════════════════════════════════════════════════════════════");
    console.log("💰 TOTALS SUMMARY:");
    console.log("─────────────────────────────────────────────────────────────────");
    console.log(`Base Grand Total:              £${baseGrandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

    if (includePreliminaries) {
      console.log(`+ Total Preliminaries:         £${totalPreliminaries.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    }

    if (includeOhp) {
      console.log(`+ Total OH&P:                  £${totalOhp.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    }

    console.log(`─────────────────────────────────────────────────────────────────`);
    console.log(`= Grand Total with Adjusts:    £${grandTotalWithAdjustments.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

    if (includeDiscount) {
      console.log(`- Discount (${discountPercent}%):             £${discountAmount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`─────────────────────────────────────────────────────────────────`);
    }

    console.log(`🎯 FINAL TENDER TOTAL:         £${tenderTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    console.log("═════════════════════════════════════════════════════════════════");
    console.log("✅ SUM ANALYSIS CALCULATION COMPLETE\n");
  }

  return {
    sections,
    baseGrandTotal,
    totalPreliminaries,
    totalOhp,
    grandTotalWithAdjustments,
    discountAmount,
    tenderTotal,
    preliminariesPercent,
    ohpPercent,
    discountPercent,
    calculatedAt,
  };
}

/**
 * Format currency value for display
 */
export function formatCurrency(value: number): string {
  return `£${value.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Get section detail by step name
 */
export function getSectionDetail(
  result: SumAnalysisGrandTotalResult,
  stepName: string
): SectionCalculationDetail | undefined {
  return result.sections.find((section) => section.stepName === stepName);
}

/**
 * Export calculation result as JSON
 */
export function exportCalculationAsJSON(
  result: SumAnalysisGrandTotalResult
): string {
  return JSON.stringify(result, null, 2);
}

/**
 * Print a summary table to console
 */
export function logSummaryTable(result: SumAnalysisGrandTotalResult): void {
  console.log("\n📋 QUICK SUMMARY TABLE:");
  console.log("┌─────────────────────────────────────┬──────────────────┐");
  console.log("│ Description                         │ Amount           │");
  console.log("├─────────────────────────────────────┼──────────────────┤");

  result.sections.forEach((section) => {
    const name = section.sectionName.padEnd(35);
    const amount = formatCurrency(section.totalAmount).padStart(16);
    console.log(`│ ${name} │ ${amount} │`);
  });

  console.log("├─────────────────────────────────────┼──────────────────┤");

  if (result.discountAmount > 0) {
    const discountLabel = `DISCOUNT (${result.discountPercent}%)`.padEnd(35);
    const discountAmt = `-${formatCurrency(result.discountAmount)}`.padStart(16);
    console.log(`│ ${discountLabel} │ ${discountAmt} │`);
    console.log("├─────────────────────────────────────┼──────────────────┤");
  }

  const tenderLabel = "TENDER TOTAL".padEnd(35);
  const tenderAmt = formatCurrency(result.tenderTotal).padStart(16);
  console.log(`│ ${tenderLabel} │ ${tenderAmt} │`);
  console.log("└─────────────────────────────────────┴──────────────────┘\n");
}
